import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { audits } from '../api/audits';
import {
  reports,
  type FilterOption,
  type VisitFilter,
  type MailDelivery,
  type VisitResultRow,
  type VisitResults as Results,
} from '../api/reports';
import {
  IconArrowDown,
  IconArrowUp,
  IconCircleAlert,
  IconClipboardList,
  IconFileText,
  IconDownload,
  IconListFilter,
  IconRefreshCw,
  IconTriangleAlert,
} from '../icons';
import { formatDate } from '../i18n';
import { describeError, useTranslate } from '../i18n/react';
import { Badge } from '../ui/Badge';
import { BandChip } from '../ui/BandChip';
import { Button, IconButton } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { Alert, Field, Select, TextInput } from '../ui/Field';
import { Card, PageHeader } from '../ui/Page';
import { Table, type Column } from '../ui/Table';

/**
 * Ziyaret Sonuç Raporu — the one report people actually open.
 *
 * <p>The system this replaces had eight reports and the customer used one, which is why
 * there is a single screen here and all of the work went into filtering it. The columns are
 * the ones they read today: who is responsible for the store, what it scored, what it
 * scored last time, and which way that moved.</p>
 */

/** The first and last day of the month that is ending, which is what a report is usually about. */
function thisMonth(): { from: string; to: string } {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  return { from: iso(first), to: iso(last) };
}

/**
 * The filter the address bar asks for, if it asks for anything.
 *
 * <p>When it does, the month is <b>not</b> applied on top. Somebody arriving from the map
 * after clicking a store wants that store's visits, and a default date range would answer
 * "no visits" for a store last audited in the spring — which is the one thing they are
 * here to find out.</p>
 */
function fromUrl(params: URLSearchParams): VisitFilter | null {
  const keys = [
    'from', 'to', 'operationId', 'regionId', 'locationId',
    'locationTypeId', 'auditorUserId', 'visitTypeId', 'belowScore',
  ] as const;

  const filter: VisitFilter = {};
  let found = false;

  for (const key of keys) {
    const value = params.get(key);
    if (!value) continue;

    found = true;
    if (key === 'belowScore') filter.belowScore = Number(value);
    else filter[key] = value;
  }

  return found ? filter : null;
}

export function VisitResults() {
  const t = useTranslate();

  const [params] = useSearchParams();
  const [first] = useState(() => fromUrl(params) ?? thisMonth());

  const [filter, setFilter] = useState<VisitFilter>(first);
  const [applied, setApplied] = useState<VisitFilter>(first);

  const [results, setResults] = useState<Results | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drawing, setDrawing] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async (f: VisitFilter) => {
    setLoading(true);
    try {
      setResults(await reports.visits(f));
      setFailed(false);
      setError(null);
    } catch (e) {
      setFailed(true);
      setError(describeError(e, 'error.listFailed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(applied);
  }, [load, applied]);

  // The dropdowns come with the report, narrowed to what this reader can reach. Fetching
  // them from the definition screens instead would answer 403 for the people this report
  // is for, and would offer them stores they cannot see.
  const options = results?.filters;

  // Regions belong to operations, so choosing one narrows the other.
  const regionsShown = filter.operationId
    ? (options?.regions ?? []).filter((r) => options?.regionOperations[r.id] === filter.operationId)
    : options?.regions ?? [];

  const locationsShown = filter.regionId
    ? (options?.locations ?? []).filter((l) => options?.locationRegions[l.id] === filter.regionId)
    : options?.locations ?? [];

  function set(change: Partial<VisitFilter>) {
    setFilter((f) => {
      const next = { ...f, ...change };

      if (change.operationId !== undefined) {
        next.regionId = undefined;
        next.locationId = undefined;
      }
      if (change.regionId !== undefined) next.locationId = undefined;

      return next;
    });
  }

  async function exportExcel() {
    if (exporting) return;

    setExporting(true);
    try {
      // The filter that was applied, not the one half-typed in the boxes above: the file
      // has to match the table somebody was looking at when they clicked.
      const { blob, fileName } = await reports.excel(applied);

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName ?? 'ziyaret-sonuclari.xlsx';
      link.click();

      // The blob stays in memory until it is let go, and a few of these is a few megabytes
      // somebody never gets back.
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(describeError(e, 'error.exportFailed'));
    } finally {
      setExporting(false);
    }
  }

  async function openReport(id: string) {
    if (drawing) return;

    setDrawing(id);
    try {
      const { url } = await audits.report(id);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setError(describeError(e, 'error.reportFailed'));
    } finally {
      setDrawing(null);
    }
  }

  const columns: Column<VisitResultRow>[] = [
    {
      id: 'location',
      header: t('visits.location'),
      className: 'is-primary',
      cell: (row) => (
        <>
          <span className="nv-code">{row.locationCode}</span> {row.locationName}
          <div className="nv-hint">
            {[row.region, row.locationType].filter(Boolean).join(' · ') || '—'}
          </div>
        </>
      ),
      skeletonWidth: '70%',
    },
    {
      id: 'manager',
      header: t('visits.regionalManager'),
      className: 'is-muted',
      cell: (row) => row.regionalManager ?? '—',
      skeletonWidth: '50%',
    },
    {
      id: 'visitType',
      header: t('visits.visitType'),
      className: 'is-muted',
      cell: (row) => (
        <>
          {row.visitType ?? '—'}
          <div className="nv-hint">{row.formName}</div>
        </>
      ),
      skeletonWidth: '60%',
    },
    {
      id: 'auditor',
      header: t('visits.auditor'),
      className: 'is-muted',
      cell: (row) => row.auditorName,
      skeletonWidth: '48%',
    },
    {
      id: 'when',
      header: t('visits.date'),
      className: 'is-muted',
      cell: (row) => (row.submittedAt ? formatDate(row.submittedAt) : '—'),
      skeletonWidth: '80px',
    },
    {
      id: 'score',
      header: t('visits.score'),
      align: 'right',
      cell: (row) => <Score row={row} />,
      skeletonWidth: '70px',
    },
    {
      id: 'previous',
      header: t('visits.previous'),
      align: 'right',
      cell: (row) =>
        row.previousScore === null ? (
          <span className="nv-subtle">{t('visits.firstVisit')}</span>
        ) : (
          <span className="nv-badge-group nv-score-cell">
            <span>{row.previousScore}</span>
            {row.previousBand ? <BandChip band={row.previousBand} colour={null} /> : null}
          </span>
        ),
      skeletonWidth: '64px',
    },
    {
      id: 'trend',
      header: t('visits.trend'),
      align: 'right',
      cell: (row) => <Trend row={row} label={t(`visits.trend.${row.trend}`)} />,
      skeletonWidth: '24px',
    },
    {
      id: 'mail',
      header: t('visits.mail'),
      className: 'is-muted',
      cell: (row) => <Mail delivery={row.mail} />,
      skeletonWidth: '64px',
    },
    {
      id: 'report',
      header: '',
      align: 'right',
      cell: (row) => (
        <IconButton
          label={t('audits.report')}
          icon={IconFileText}
          disabled={drawing === row.id}
          onClick={() => void openReport(row.id)}
        />
      ),
      skeletonWidth: '24px',
    },
  ];

  const summary = results?.summary;

  return (
    <>
      <PageHeader
        crumbs={[t('app.reports')]}
        title={t('visits.title')}
        subtitle={t('visits.subtitle')}
      />

      {error ? <Alert>{error}</Alert> : null}

      <Card className="nv-pad">
        <div className="nv-form-grid">
          <Field label={t('visits.from')}>
            {({ id }) => (
              <TextInput
                id={id}
                type="date"
                value={filter.from ?? ''}
                onChange={(e) => set({ from: e.target.value })}
              />
            )}
          </Field>

          <Field label={t('visits.to')}>
            {({ id }) => (
              <TextInput
                id={id}
                type="date"
                value={filter.to ?? ''}
                onChange={(e) => set({ to: e.target.value })}
              />
            )}
          </Field>

          <Picker
            label={t('visits.operation')}
            value={filter.operationId}
            onChange={(v) => set({ operationId: v })}
            options={options?.operations ?? []}
            all={t('visits.all')}
          />

          <Picker
            label={t('visits.region')}
            value={filter.regionId}
            onChange={(v) => set({ regionId: v })}
            options={regionsShown}
            all={t('visits.all')}
          />

          <Picker
            label={t('visits.locationFilter')}
            value={filter.locationId}
            onChange={(v) => set({ locationId: v })}
            options={locationsShown}
            all={t('visits.all')}
          />

          <Picker
            label={t('visits.locationType')}
            value={filter.locationTypeId}
            onChange={(v) => set({ locationTypeId: v })}
            options={options?.locationTypes ?? []}
            all={t('visits.all')}
          />

          <Picker
            label={t('visits.auditor')}
            value={filter.auditorUserId}
            onChange={(v) => set({ auditorUserId: v })}
            options={options?.auditors ?? []}
            all={t('visits.all')}
          />

          <Picker
            label={t('visits.visitType')}
            value={filter.visitTypeId}
            onChange={(v) => set({ visitTypeId: v })}
            options={options?.visitTypes ?? []}
            all={t('visits.all')}
          />

          <Field label={t('visits.below')} hint={t('visits.belowHint')}>
            {({ id }) => (
              <TextInput
                id={id}
                type="number"
                min={0}
                max={100}
                value={filter.belowScore ?? ''}
                onChange={(e) =>
                  set({ belowScore: e.target.value === '' ? undefined : Number(e.target.value) })
                }
              />
            )}
          </Field>
        </div>

        <div className="nv-actions-bar">
          <Button variant="primary" icon={IconListFilter} onClick={() => setApplied({ ...filter })}>
            {t('visits.apply')}
          </Button>
          <Button
            icon={IconDownload}
            disabled={exporting || loading || (results?.rows.length ?? 0) === 0}
            onClick={() => void exportExcel()}
          >
            {t('visits.excel')}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              const fresh = thisMonth();
              setFilter(fresh);
              setApplied(fresh);
            }}
          >
            {t('visits.reset')}
          </Button>
        </div>
      </Card>

      {summary ? (
        <div className="nv-stat-row">
          <div className="nv-stat">
            <span className="nv-stat__label">{t('visits.count')}</span>
            <strong className="nv-stat__value">{summary.count}</strong>
          </div>
          <div className="nv-stat">
            <span className="nv-stat__label">{t('visits.average')}</span>
            <strong className="nv-stat__value">{summary.averageScore ?? '—'}</strong>
          </div>
          <div className="nv-stat">
            <span className="nv-stat__label">{t('visits.zeroed')}</span>
            <strong className="nv-stat__value">{summary.zeroedByCritical}</strong>
          </div>
          {filter.belowScore !== undefined ? (
            <div className="nv-stat">
              <span className="nv-stat__label">
                {t('visits.belowCount', { score: String(filter.belowScore) })}
              </span>
              <strong className="nv-stat__value">{summary.belowThreshold}</strong>
            </div>
          ) : null}
        </div>
      ) : null}

      {results?.truncated ? <Alert tone="warning">{t('visits.truncated')}</Alert> : null}

      <Card className="nv-table-card">
        <Table
          caption={t('visits.title')}
          columns={columns}
          rows={results?.rows ?? []}
          getRowId={(row) => row.id}
          status={loading ? 'loading' : failed ? 'error' : 'ready'}
          errorState={
            <EmptyState
              icon={IconCircleAlert}
              tone="danger"
              title={t('common.loadFailed')}
              text={t('common.loadFailedText')}
              action={
                <Button icon={IconRefreshCw} onClick={() => void load(applied)}>
                  {t('common.retry')}
                </Button>
              }
            />
          }
          emptyState={
            <EmptyState
              icon={IconClipboardList}
              title={t('visits.empty')}
              text={t('visits.emptyText')}
            />
          }
        />
      </Card>
    </>
  );
}

/**
 * What became of the report mail.
 *
 * <p>A visit type that names nobody shows a dash rather than a tone: nothing was supposed
 * to happen, and a grey "not sent" would read as a failure.</p>
 */
function Mail({ delivery }: { delivery: MailDelivery }) {
  const t = useTranslate();
  const label = t(`visits.mail.${delivery}`);

  if (delivery === 'None') return <span className="nv-subtle" title={label}>—</span>;

  const tone = delivery === 'Sent' ? 'comment' : delivery === 'Failed' ? 'critical' : 'na';

  return <Badge tone={tone}>{label}</Badge>;
}

/** The score, its band, and the fact that a critical question took it to zero. */
function Score({ row }: { row: VisitResultRow }) {
  const t = useTranslate();

  if (row.score === null) return <span className="nv-subtle">—</span>;

  return (
    <span className="nv-badge-group nv-score-cell">
      <strong>{row.score}</strong>
      {row.band ? <BandChip band={row.band} colour={row.bandColour} /> : null}
      {row.zeroedByCritical ? (
        <Badge tone="critical" icon={IconTriangleAlert}>{t('audits.criticalFailed')}</Badge>
      ) : null}
    </span>
  );
}

/**
 * Which way the store moved.
 *
 * <p>A first visit shows a dash rather than a flat arrow: there is nothing to be level
 * with, and an arrow would say there was.</p>
 */
function Trend({ row, label }: { row: VisitResultRow; label: string }) {
  if (row.trend === 'Unknown') return <span className="nv-subtle" title={label}>—</span>;

  const difference =
    row.score !== null && row.previousScore !== null
      ? Math.round((row.score - row.previousScore) * 100) / 100
      : null;

  if (row.trend === 'Steady') {
    return (
      <span className="nv-trend is-steady" title={label}>
        <span aria-hidden>=</span>
        <span className="nv-sr-only">{label}</span>
      </span>
    );
  }

  const up = row.trend === 'Improved';
  const Icon = up ? IconArrowUp : IconArrowDown;

  return (
    <span className={`nv-trend ${up ? 'is-up' : 'is-down'}`} title={label}>
      <Icon size={16} />
      {difference !== null ? (
        <span className="nv-trend__delta">{up ? `+${difference}` : difference}</span>
      ) : null}
      <span className="nv-sr-only">{label}</span>
    </span>
  );
}

/** A select whose empty value means "all of them", which is what every filter here does. */
function Picker({
  label,
  value,
  onChange,
  options,
  all,
}: {
  label: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  options: FilterOption[];
  all: string;
}) {
  return (
    <Field label={label}>
      {({ id }) => (
        <Select
          id={id}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
        >
          <option value="">{all}</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.text}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}
